/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, type ForwardRefExoticComponent, type RefAttributes } from 'react';

import { css, jsx } from '@atlaskit/css';
import PersonIcon from '@atlaskit/icon/core/person';
import { token } from '@atlaskit/tokens';

const sizeMap = {
	xsmall: 16,
	small: 24,
	medium: 32,
	large: 40,
	xlarge: 96,
	xxlarge: 128,
};

type SizeType = keyof typeof sizeMap;

const avatarStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	borderRadius: token('radius.full', '50%'),
	backgroundColor: token('color.background.neutral.subtle', '#EBECF0'),
	overflow: 'hidden',
	position: 'relative',
});

const imgStyles = css({
	height: '100%',
	width: '100%',
	objectFit: 'cover',
});

export interface MinimalAvatarPropTypes {
	/**
	 * Defines the size of the avatar. Default value is `medium`.
	 */
	size?: SizeType;
	/**
	 * A url to load an image from (this can also be a base64 encoded image).
	 */
	src?: string;
}

/**
 * __Avatar (Minimal)__
 *
 * A minimal avatar component for displaying user profile images.
 * This is a simplified version that only supports size and src props.
 *
 * - [Examples](https://atlassian.design/components/avatar/examples)
 * - [Code](https://atlassian.design/components/avatar/code)
 */
const Avatar: ForwardRefExoticComponent<MinimalAvatarPropTypes & RefAttributes<HTMLDivElement>> =
	forwardRef<HTMLDivElement, MinimalAvatarPropTypes>(({ size = 'medium', src }, ref) => {
		const dimension = sizeMap[size];
		return (
			<div
				css={avatarStyles}
				style={{
					height: `${dimension}px`,
					width: `${dimension}px`,
				}}
				ref={ref}
			>
				{src ? (
					<img css={imgStyles} src={src} alt="" />
				) : (
					<PersonIcon color={token('color.icon.subtle')} label="" spacing="spacious" />
				)}
			</div>
		);
	});

Avatar.displayName = 'Avatar';

export default Avatar;
